import { useEffect, useMemo, useState } from "react";
import { Card, Cluster, FormField, Grid, Input, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface AccessEntry {
  time: string;
  source: "private" | "public";
  ip: string;
  requester: string;
  operation: string;
  key: string;
  status: number;
  error_code: string;
  bytes_sent: number;
  auth: string;
  user_agent: string;
}

interface Finding {
  detected_at: string | null;
  source: string | null;
  rule: string | null;
  ip: string | null;
  detail: string | null;
}

interface TopIp {
  ip: string;
  requests: number;
  bytes: number;
  denied: number;
}

interface StorageAccess {
  hours: number;
  source: string;
  files_read: number;
  truncated: boolean;
  summary: {
    requests: number;
    distinct_ips: number;
    bytes_sent: number;
    client_errors: number;
    downloads: number;
    top_ips: TopIp[];
  };
  entries: AccessEntry[];
  findings: Finding[];
}

const RULE_LABEL: Record<string, string> = {
  unexpected_ip: "Unexpected IP",
  download_pattern: "Unusual downloads",
  unexpected_write: "Unexpected write",
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function accessPath(filters: { source: string; hours: string; ip: string }): string {
  const params = new URLSearchParams();
  params.set("limit", "200");
  params.set("hours", filters.hours);
  if (filters.source !== "all") params.set("source", filters.source);
  if (filters.ip) params.set("ip", filters.ip);
  return `/operator/storage-access?${params.toString()}`;
}

export function StorageAccessPage() {
  const [data, setData] = useState<StorageAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [hours, setHours] = useState("24");
  const [ipInput, setIpInput] = useState("");
  const [ip, setIp] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => setIp(ipInput.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [ipInput]);

  const filters = useMemo(() => ({ source, hours, ip }), [source, hours, ip]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<StorageAccess>(accessPath(filters))
      .then((row) => {
        if (!cancelled) setData(row);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load storage access logs.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const summary = data?.summary;
  const entries = data?.entries ?? [];
  const findings = data?.findings ?? [];

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <h2>Storage access</h2>
        <p className="admin-muted">
          Every request to the private and public file buckets, from the S3 access logs. Alerts for unusual
          downloads or unexpected addresses are emailed to support@saleslox.com as they are detected.
          Logs arrive a few minutes after the request, so this is an audit trail, not a live feed.
        </p>
      </Stack>
      {error && <Flash tone="error">{error}</Flash>}
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Alerts"
            tip="Findings raised by the access monitor in the last 7 days: credentialed requests from an address outside the expected ranges, one address pulling an unusual number or volume of files, or writes by something other than the app. Each one was also emailed."
          />
          <table>
            <thead>
              <tr>
                <th>Detected (UTC)</th>
                <th>Bucket</th>
                <th>Rule</th>
                <th>IP</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, index) => (
                <tr key={`${f.detected_at}-${f.ip}-${f.rule}-${index}`}>
                  <td className="admin-readonly">{formatUtc(f.detected_at)}</td>
                  <td>{f.source ?? "—"}</td>
                  <td>{RULE_LABEL[f.rule ?? ""] ?? f.rule ?? "—"}</td>
                  <td><code>{f.ip ?? "—"}</code></td>
                  <td>{f.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && findings.length === 0 && !error && (
            <p className="admin-muted">No alerts in the last 7 days.</p>
          )}
        </Stack>
      </Card>
      <Grid columns={4} gap={3}>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Requests" tip="Requests in the selected window and bucket filter." />
            <strong>{summary?.requests ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Downloads" tip="Successful object reads (signed or anonymous)." />
            <strong>{summary?.downloads ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Distinct IPs" tip="Different source addresses in the window." />
            <strong>{summary?.distinct_ips ?? "—"}</strong>
          </Stack>
        </Card>
        <Card>
          <Stack gap={2}>
            <TipKicker label="Data sent" tip="Total bytes returned to callers in the window." />
            <strong>{summary ? formatBytes(summary.bytes_sent) : "—"}</strong>
          </Stack>
        </Card>
      </Grid>
      <Card>
        <Cluster gap={3} align="end">
          <FormField label="Bucket" tooltip="Private holds customer files (presigned links only). Public holds world-readable assets.">
            <Select aria-label="Filter bucket" value={source} onChange={(event) => setSource(event.target.value)}>
              <option value="all">Both</option>
              <option value="private">Private</option>
              <option value="public">Public</option>
            </Select>
          </FormField>
          <FormField label="Window" tooltip="How far back to read the access logs. Longer windows read more log files and load slower.">
            <Select aria-label="Filter window" value={hours} onChange={(event) => setHours(event.target.value)}>
              <option value="1">Last hour</option>
              <option value="6">Last 6 hours</option>
              <option value="24">Last 24 hours</option>
              <option value="72">Last 3 days</option>
            </Select>
          </FormField>
          <FormField label="IP address" tooltip="Show only requests from this exact address.">
            <Input
              aria-label="Filter IP"
              value={ipInput}
              onChange={(event) => setIpInput(event.target.value)}
              placeholder="203.0.113.10"
            />
          </FormField>
        </Cluster>
      </Card>
      {summary && summary.top_ips.length > 0 && (
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Busiest addresses"
              tip="Source IPs with the most requests in the window. Denied counts 401/403 responses, a sign of probing."
            />
            <table>
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Requests</th>
                  <th>Data sent</th>
                  <th>Denied</th>
                </tr>
              </thead>
              <tbody>
                {summary.top_ips.map((row) => (
                  <tr key={row.ip}>
                    <td><code>{row.ip}</code></td>
                    <td>{row.requests}</td>
                    <td>{formatBytes(row.bytes)}</td>
                    <td>{row.denied}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      )}
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Access log"
            tip="Most recent requests first. 'Signed request' means the app's own credentials; 'presigned URL' is a customer download link; 'anonymous' is a public-bucket read."
          />
          <table>
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Bucket</th>
                <th>IP</th>
                <th>Operation</th>
                <th>Object</th>
                <th>Status</th>
                <th>Size</th>
                <th>How</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row, index) => (
                <tr key={`${row.time}-${row.ip}-${row.key}-${index}`}>
                  <td className="admin-readonly">{formatUtc(row.time)}</td>
                  <td>{row.source}</td>
                  <td><code>{row.ip}</code></td>
                  <td>{row.operation.replace("REST.", "")}</td>
                  <td><code>{row.key || "—"}</code></td>
                  <td>{row.status}{row.error_code ? ` ${row.error_code}` : ""}</td>
                  <td>{formatBytes(row.bytes_sent)}</td>
                  <td>{row.auth}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && entries.length === 0 && !error && (
            <p className="admin-muted">No requests logged in this window.</p>
          )}
          {data?.truncated && (
            <p className="admin-muted">
              Showing the newest {data.files_read} log files; narrow the window to see older requests.
            </p>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
